import React, { memo } from 'react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemButton,
  Typography,
  IconButton,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon, Lock as LockIcon, Edit as EditIcon } from '@mui/icons-material';

const listItemButtonSx = {
  flexDirection: 'column',
  alignItems: 'flex-start',
  py: 1,
  px: 1.5,
  gap: 0.5,
  '&.Mui-selected': {
    bgcolor: 'primary.light',
    '&:hover': { bgcolor: 'primary.light' },
  },
};

/** 읽기전용/실습 파일용 Chip 스타일 */
const readonlyChipSx = {
  height: 20,
  fontSize: '0.65rem',
  fontWeight: 600,
  bgcolor: 'grey.200',
  color: 'grey.700',
  '& .MuiChip-icon': { fontSize: 12 },
};

const practiceChipSx = {
  height: 20,
  fontSize: '0.65rem',
  fontWeight: 600,
  bgcolor: 'warning.light',
  color: 'white',
  '& .MuiChip-icon': { fontSize: 12 },
};

const paperSx = {
  width: 260,
  overflow: 'auto',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
};

const contentPaperSx = {
  flex: 1,
  overflow: 'hidden',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  display: 'flex',
  flexDirection: 'column',
};

/**
 * 파일을 정규화 (content/fileContent, isReadonly/readonly 통일)
 */
const normalizeFile = (f, idx) => ({
  id: f.id ?? `file-${idx}`,
  fileName: f.fileName ?? `파일 ${idx + 1}`,
  fileContent: f.fileContent ?? f.content ?? '',
  readonly: f.readonly ?? f.isReadonly ?? false,
});

/**
 * 파일 탐색기 - 상세보기 모드 (읽기 전용 라벨만 표시)
 */
const FileListItemView = memo(({ file, isSelected, onSelect }) => {
  const normalized = normalizeFile(file, 0);
  const isReadonly = normalized.readonly;
  return (
    <ListItem
      disablePadding
      sx={{
        borderLeft: '3px solid',
        borderColor: isReadonly ? 'grey.400' : 'success.main',
      }}
    >
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelect(file)}
        sx={{
          ...listItemButtonSx,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontWeight: isSelected ? 600 : 400,
            color: isSelected ? 'primary.main' : 'text.primary',
          }}
        >
          {normalized.fileName}
        </Typography>
        {isReadonly ? (
          <Chip
            icon={<LockIcon />}
            label="읽기전용"
            size="small"
            sx={readonlyChipSx}
          />
        ) : (
          <Chip
            icon={<EditIcon />}
            label="실습 파일"
            size="small"
            sx={practiceChipSx}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
});

/**
 * 파일 탐색기 - 수정 모드 (삭제, 수정가능 스위치)
 */
const FileListItemEdit = memo(({ file, idx, isSelected, onSelect, onRemove }) => {
  const normalized = normalizeFile(file, idx);
  const isReadonly = normalized.readonly;
  return (
    <ListItem
      disablePadding
      sx={{
        borderLeft: '3px solid',
        borderColor: isReadonly ? 'grey.400' : 'success.main',
      }}
      secondaryAction={
        <IconButton edge="end" size="small" onClick={() => onRemove(idx)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      }
    >
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelect(idx)}
        sx={{
          ...listItemButtonSx,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.875rem',
            fontWeight: isSelected ? 600 : 400,
            color: isSelected ? 'primary.main' : 'text.primary',
          }}
        >
          {normalized.fileName}
        </Typography>
        {isReadonly ? (
          <Chip
            icon={<LockIcon />}
            label="읽기전용"
            size="small"
            sx={readonlyChipSx}
          />
        ) : (
          <Chip
            icon={<EditIcon />}
            label="실습 파일"
            size="small"
            sx={practiceChipSx}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
});

/**
 * 공통 파일 탐색기 컴포넌트
 * @param {Object} props
 * @param {Array} props.files - 파일 목록
 * @param {number|string} props.activeKey - 선택된 파일 (인덱스 또는 id)
 * @param {'id'|'index'} props.selectionMode - 'id': 파일 id로 선택, 'index': 인덱스로 선택
 * @param {'view'|'edit'} props.mode - view: 읽기전용, edit: 수정 가능
 * @param {Function} props.onSelectFile - 파일 선택 시 (id 또는 idx)
 * @param {Function} [props.onRemoveFile] - edit 모드에서 파일 삭제 (idx)
 * @param {Function} [props.onEditableChange] - edit 모드에서 수정가능 토글 (idx, isEditable)
 * @param {React.ReactNode} props.children - 에디터 영역 (또는 renderEditor)
 */
const FileExplorer = memo(({
  files = [],
  activeKey,
  selectionMode = 'index',
  mode = 'view',
  onSelectFile,
  onRemoveFile,
  headerTitle = '파일 탐색기',
  children,
}) => {
  const isSelected = (file, idx) =>
    selectionMode === 'id' ? file.id === activeKey : idx === activeKey;

  return (
    <Box sx={{ display: 'flex', gap: 2, minHeight: 500 }}>
      <Paper sx={paperSx}>
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {headerTitle}
          </Typography>
        </Box>
        <List dense sx={{ p: 0 }}>
          {files.map((file, idx) =>
            mode === 'view' ? (
              <FileListItemView
                key={file.id ?? idx}
                file={file}
                isSelected={isSelected(file, idx)}
                onSelect={() => onSelectFile(selectionMode === 'id' ? file.id : idx)}
              />
            ) : (
              <FileListItemEdit
                key={file.id ?? idx}
                file={file}
                idx={idx}
                isSelected={isSelected(file, idx)}
                onSelect={onSelectFile}
                onRemove={onRemoveFile}
              />
            )
          )}
        </List>
      </Paper>

      <Paper sx={contentPaperSx}>
        {children}
      </Paper>
    </Box>
  );
});

FileExplorer.displayName = 'FileExplorer';

export default FileExplorer;
